import { type BaseSyntheticEvent, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { Show } from '@elfsight/lib-utils';
import type {
  FieldComponentProps,
  FormField
} from '@elfsight/widget-form-builder/widget';
import {
  FieldsGrid as LibFieldsGrid,
  SubmitButton as LibSubmitButton,
  SubmitMessage as LibSubmitMessage,
  useEmitFormSubmitEvent,
  useFormSelector,
  useGetCountries,
  useRegisterFormService,
  useWidgetSelector
} from '@elfsight/widget-form-builder/widget';
import { Loader as LibLoader, Typography } from '@elfsight/widget-ui';
import { createReadableClass } from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import { EmptyMessage } from '../../main';
import { useFormPopup } from '../form-popup-context';
import { useInvalidFieldErrorMessageGetter } from '../utils';

interface FormLayoutProps {
  fields: FormField[];
  displayedFields: FormField[];
  onSubmit: (e: BaseSyntheticEvent) => Promise<string | undefined>;
}

export function FormLayout({
  fields,
  displayedFields,
  onSubmit
}: FormLayoutProps) {
  const { t } = useLingui();
  const { countries } = useGetCountries();

  const getInvalidFieldErrorMessage = useInvalidFieldErrorMessageGetter();

  const {
    language,
    submitButtonText,
    submitAction,
    submitMessageTitle,
    submitMessageText,
    submitMessageButton,
    submitMessageButtonText,
    recaptcha,
    formHeading,
    formCaption
  } = useSettings();

  const derivedFields: FormField[] =
    formHeading || formCaption
      ? [
          {
            id: 'heading',
            type: 'heading',
            heading: formHeading,
            caption: formCaption
          },
          ...displayedFields
        ]
      : displayedFields;
  const hasFields = !!fields.length;

  const [error, setError] = useState<string>();
  const { submitted } = useWidgetSelector();
  const { status = '', fieldsError } = useFormSelector();
  const emitSubmitEvent = useEmitFormSubmitEvent();

  const { closePopup } = useFormPopup();

  useRegisterFormService(fields);

  const handleSubmit = async (e: BaseSyntheticEvent) => {
    const submitError = await onSubmit(e);
    if (!submitError) {
      emitSubmitEvent(fields);
    }
  };

  useEffect(() => {
    if (status === 'failed') {
      setError(t`An error occurred when submitting the form, please try again`);
      return;
    }

    const { required, invalid } = fieldsError;
    if (required) {
      setError(t`Please fill all the required fields`);
      return;
    }

    if (invalid) {
      setError(t`Please fill all the fields correctly`);
      return;
    }

    setError(undefined);
  }, [status, fieldsError, t]);

  if (submitAction === 'message' && submitted && status === 'success') {
    return (
      <SubmitMessage
        fields={displayedFields}
        submitMessageTitle={submitMessageTitle}
        submitMessageText={submitMessageText}
        submitMessageButton={submitMessageButton}
        submitMessageButtonText={submitMessageButtonText}
        onSubmitMessageButtonClick={closePopup}
        fullWidth
      />
    );
  }

  return (
    <Container onSubmit={(event) => event.preventDefault()}>
      <FieldsGrid
        fields={derivedFields}
        widgetLanguage={language.language}
        getInvalidErrorMessage={getInvalidFieldErrorMessage}
        requiredErrorMessage={t`This field is required`}
        componentFieldProps={{
          file: {
            extensionErrorMessage: t`This file cannot be uploaded because the file format is unsupported`,
            unexpectedErrorMessage: t`An unexpected error occurred while uploading the file. Please try again`,
            dropFileText: t`Drop file here`,
            chooseFileText: t`Choose file`,
            orDropFileText: t`or drop here`
          },
          phone: {
            searchPlaceholderText: t`Search country...`,
            emptyCountryListMessage: t`No countries found`,
            ariaLabelCountryDropdown: t`Country selection list`,
            ariaLabelCountryDropdownTrigger: t`Toggle country selection`,
            portalContainerElement: document.body,
            countries
          }
        }}
        addBottomMarginToLastChild
      />

      <Show when={!hasFields}>
        <StyledEmptyMessage>{t`Add at least one field to your form.`}</StyledEmptyMessage>
      </Show>

      <Show when={hasFields}>
        <Show when={status !== 'pending'} fallback={<Loader />}>
          <SubmitButtonContainer>
            <SubmitButton text={submitButtonText} onClick={handleSubmit} />
          </SubmitButtonContainer>
        </Show>
      </Show>

      <Show when={['failed', 'invalid'].includes(status) && !!error}>
        <ErrorsGroup>
          <Error>{error}</Error>
        </ErrorsGroup>
      </Show>

      <Show when={recaptcha}>
        <Recaptcha
          dangerouslySetInnerHTML={{
            __html: t`This site is protected by reCAPTCHA and the Google <a href="https://policies.google.com/privacy" target="_blank" rel="nofollow noreferrer noopener">Privacy Policy</a> and <a href="https://policies.google.com/terms" target="_blank" rel="nofollow noreferrer noopener">Terms of Service</a> apply.`
          }}
        />
      </Show>
    </Container>
  );
}

export const getJustifyContent = () => {
  return css`
    justify-content: ${({
      theme: {
        form: { submitButtonAlignment }
      }
    }) =>
      submitButtonAlignment === 'right' ? 'flex-end' : submitButtonAlignment};
  `;
};

const Container = styled.form.attrs({
  className: createReadableClass({ component: 'form', element: 'container' })
})``;

const Error = styled(Typography).attrs(
  ({
    theme: {
      form: { errorColor }
    }
  }) => ({
    className: createReadableClass({ component: 'form', element: 'error' }),
    color: errorColor,
    textStyle: {
      fontSize: 14
    }
  })
)``;

const ErrorsGroup = styled.div.attrs({
  className: createReadableClass({ component: 'form', element: 'errors-group' })
})`
  display: flex;
  margin-top: 12px;
  ${getJustifyContent};
`;

const SubmitMessage = styled(LibSubmitMessage).attrs(
  ({
    theme: {
      form: {
        submitMessageTitleFontStyle,
        submitMessageTextFontStyle,
        submitMessageButtonColor,
        submitMessageAlignment,
        submitMessageTextColor,
        submitMessageTitleColor,
        submitButtonProps
      }
    }
  }) => ({
    fullWidth: false,
    gap: 8,
    submitMessageAlignment,
    submitMessageTextColor,
    submitMessageTitleColor,
    submitMessageTitleFontStyle,
    submitMessageTextFontStyle,
    submitMessageButtonColor,
    submitMessageButtonBorderRadius: submitButtonProps?.borderRadius,
    submitMessageButtonStyle: {
      marginTop: 24,
      minWidth: 140
    },
    className: createReadableClass({
      component: 'form',
      element: 'submit-message'
    })
  })
)``;

const Loader = styled(LibLoader).attrs(
  ({
    theme: {
      form: { fieldActiveColor }
    }
  }) => ({
    color: fieldActiveColor,
    className: createReadableClass({ component: 'form', element: 'loader' })
  })
)``;

const FieldsGrid = styled(LibFieldsGrid).attrs(
  ({
    theme: {
      form: { errorColor, ...formSettings }
    },
    componentFieldProps
  }) => ({
    formSettings,
    errorColor,
    className: createReadableClass({
      component: 'form',
      element: 'fields-grid'
    }),
    componentFieldProps: {
      ...componentFieldProps,
      heading: {
        ...componentFieldProps?.heading,
        fieldHintColor: formSettings.fieldColor,
        fieldSpacing: 32,
        fieldHintFontStyle: {
          fontSize: 16
        }
      }
    }
  })
)<Partial<FieldComponentProps>>``;

const SubmitButtonContainer = styled.div.attrs({
  className: createReadableClass({
    component: 'form',
    element: 'submit-button-container'
  })
})`
  display: flex;
  ${getJustifyContent};
`;

const SubmitButton = styled(LibSubmitButton).attrs(
  ({
    theme: {
      form: { submitButtonProps }
    }
  }) => ({
    ...submitButtonProps,
    className: createReadableClass({
      component: 'form',
      element: 'submit-button'
    })
  })
)``;

const Recaptcha = styled(Typography).attrs(
  ({
    theme: {
      form: { widgetFooterTextColor, widgetFooterTextStyle }
    }
  }) => ({
    className: createReadableClass({
      component: 'form',
      element: 'recaptcha'
    }),
    textStyle: widgetFooterTextStyle,
    color: widgetFooterTextColor
  })
)`
  margin-top: 20px;

  & a {
    text-decoration: underline;
    cursor: pointer;
    color: ${({
      theme: {
        form: { widgetFooterLinksColor }
      }
    }) => widgetFooterLinksColor};
  }
`;

const StyledEmptyMessage = styled(EmptyMessage).attrs(
  ({
    theme: {
      form: { fieldColor }
    }
  }) => ({
    color: fieldColor
  })
)``;
